import {useCallback, useMemo, useState, type Dispatch, type SetStateAction} from "react";
import {Circle} from "react-konva";
import type {Image} from "@/images/Image.ts";
import {gridToImage, imageToGrid, snapImagePointToGrid} from "@/dungeons/geometry/Coordinates.ts";
import AnnotationMarker from "@/dungeons/annotations/AnnotationMarker.tsx";
import AnnotationPopover from "@/dungeons/annotations/AnnotationPopover.tsx";
import {annotationScreenPosition} from "@/dungeons/annotations/AnnotationPopoverPosition.ts";
import type {LevelGeometryDocument, GridPoint} from "@/dungeons/model/DungeonGeometry.ts";
import GridCalibrationOverlay from "@/dungeons/editor/grid/GridCalibrationOverlay.tsx";
import type useGridCalibration from "@/dungeons/editor/grid/useGridCalibration.ts";
import type {GeometryEditorController} from "@/dungeons/editor/geometry/useGeometryEditorController.ts";
import {snapGeometryPoint, type GeometrySnapResult} from "@/dungeons/editor/geometry/GeometrySnapping.ts";
import GeometryHitOverlays from "@/dungeons/editor/geometry/GeometryHitOverlays.tsx";
import SelectedGeometryOverlay from "@/dungeons/editor/geometry/SelectedGeometryOverlay.tsx";
import GeometryDraftOverlay from "@/dungeons/editor/geometry/GeometryDraftOverlay.tsx";
import {pointsEqual} from "@/dungeons/editor/geometry/GeometryDocumentOperations.ts";
import {validTransitionPolygon} from "@/dungeons/geometry/TransitionPolygon.ts";
import type {DungeonAnnotationsController} from "@/dungeons/editor/annotations/useDungeonAnnotations.ts";
import type {DungeonLightsController} from "@/dungeons/editor/lighting/useDungeonLights.ts";
import {useDungeonEditorHistory} from "@/dungeons/editor/history/DungeonEditorHistory.tsx";
import DungeonCanvasViewport from "./DungeonCanvasViewport.tsx";
import type {DungeonViewport} from "./DungeonViewport.ts";
import type {UnifiedEditorTool} from "./UnifiedEditorTools.ts";
import type {DirectionalLight} from "@/dungeons/lighting/DirectionalLight.ts";
import EditorLightingPreview from "../lighting/EditorLightingPreview.tsx";

export default function UnifiedDungeonCanvas({image, document, transitionTargetGeometry, transitionTargetName,
    viewport, setViewport, tool, onSelectTool,
    grid, geometry, annotations, lights, snappingEnabled, lightingPreview, directionalLight}: {
    image: Image;
    document: LevelGeometryDocument;
    transitionTargetGeometry?: LevelGeometryDocument;
    transitionTargetName?: string;
    viewport?: DungeonViewport;
    setViewport: Dispatch<SetStateAction<DungeonViewport | undefined>>;
    tool: UnifiedEditorTool;
    onSelectTool: (tool: UnifiedEditorTool, preserveInteraction?: boolean) => void;
    grid: ReturnType<typeof useGridCalibration>;
    geometry: GeometryEditorController;
    annotations: DungeonAnnotationsController;
    lights: DungeonLightsController;
    snappingEnabled: boolean;
    lightingPreview: boolean;
    directionalLight: DirectionalLight;
}) {
    const [cursorSnap, setCursorSnap] = useState<{tool: UnifiedEditorTool; snap: GeometrySnapResult}>();
    const [hoverOrigin, setHoverOrigin] = useState<GridPoint>();
    const {revision} = useDungeonEditorHistory();
    const {state, commands} = geometry;
    const [geometryPreview, setGeometryPreview] = useState<{base: LevelGeometryDocument; document: LevelGeometryDocument;
        selection: typeof state.selection}>();
    const previewGeometry = useCallback((next: LevelGeometryDocument | undefined) => {
        setGeometryPreview(next ? {base: document, document: next, selection: state.selection} : undefined);
    }, [document, state.selection]);
    const lightDocument = geometryPreview?.base === document && geometryPreview.selection === state.selection
        ? geometryPreview.document : document;
    const previewDocument = useMemo(() => ({...lightDocument, lights: lights.displayLights, directionalLight}),
        [lightDocument, lights.displayLights, directionalLight]);
    const drawing = tool === "wall" || tool === "door" || tool === "transition";
    const selecting = tool === "select";
    const annotationInteraction = selecting || tool === "annotation";
    const resolveSnap = (point: GridPoint, pixelsPerCell: number, controlKey: boolean) => snapGeometryPoint({
        point: imageToGrid(point, document.registration), document, pixelsPerCell,
        enabled: snappingEnabled && !controlKey,
    });

    return <DungeonCanvasViewport image={image} geometry={document} viewport={viewport}
        onViewportChange={setViewport}
        panButtons={["middle"]}
        showAnnotations={!annotationInteraction}
        hiddenFeature={selecting ? state.selection : undefined}
        renderLighting={lightingPreview && document.registration.calibrated ? (transform, size) =>
            <EditorLightingPreview document={previewDocument}
                image={image} transform={transform} size={size}/> : undefined}
        onMapClick={({point, pixelsPerCell, event}) => {
            if (tool === "calibrate" || tool === "origin") grid.selectPoint(point, tool);
            else if (tool === "wall") commands.addWallPoint(resolveSnap(point, pixelsPerCell, event.evt.ctrlKey).point);
            else if (tool === "transition") {
                const next = resolveSnap(point, pixelsPerCell, event.evt.ctrlKey).point;
                const first = state.transitionDraft[0];
                const cursor = imageToGrid(point, document.registration);
                const closeToStart = first && state.transitionDraft.length >= 3 &&
                    Math.hypot(cursor.x - first.x, cursor.y - first.y) * pixelsPerCell <= 10;
                if (closeToStart) {
                    if (validTransitionPolygon(state.transitionDraft)) {
                        commands.finishTransition();
                        onSelectTool("select", true);
                    }
                } else commands.addTransitionPoint(next);
            }
            else if (tool === "door") {
                const endpoint = resolveSnap(point, pixelsPerCell, event.evt.ctrlKey).point;
                const completing = Boolean(state.doorDraft && !pointsEqual(state.doorDraft, endpoint));
                commands.placeDoorPoint(endpoint);
                if (completing) onSelectTool("select", true);
            } else if (tool === "annotation") {
                annotations.place(point);
                onSelectTool("select", true);
            } else if (tool === "light") {
                lights.place(point);
                commands.select();
                annotations.close();
                onSelectTool("select", true);
            } else if (event.target.hasName("editor-map-hit")) {
                commands.select();
                annotations.close();
                lights.select(undefined);
            }
        }}
        onMapDoubleClick={() => {
            if (tool === "wall") commands.finishWall();
            if (tool === "transition" && validTransitionPolygon(state.transitionDraft)) {
                commands.finishTransition();
                onSelectTool("select", true);
            }
        }}
        onMapMove={({point, pixelsPerCell, event}) => {
            if (drawing) setCursorSnap({tool, snap: resolveSnap(point, pixelsPerCell, event.evt.ctrlKey)});
            if (tool === "origin") setHoverOrigin(snapImagePointToGrid(point, document.registration));
        }}
        onMapLeave={() => {setCursorSnap(undefined); setHoverOrigin(undefined);}}
        onContextMenu={() => {
            if (tool === "calibrate" || tool === "origin") {
                if (document.registration.calibrated) onSelectTool("select");
            } else if (tool === "wall") {
                commands.wallContextMenu();
                if (state.wallDraft.length === 0) onSelectTool("select", true);
            } else if (tool === "door") {
                commands.doorContextMenu();
                if (!state.doorDraft) onSelectTool("select", true);
            } else if (tool === "transition") {
                commands.transitionContextMenu();
                if (state.transitionDraft.length === 0) onSelectTool("select", true);
            } else if (tool === "annotation" || tool === "light") onSelectTool("select");
        }}
        renderInteraction={zoom => <>
            {(tool === "calibrate" || tool === "origin") && <GridCalibrationOverlay
                calibrationPoints={grid.calibrationPoints}
                registeredOrigin={document.registration.calibrated ? {
                    x: document.registration.gridOffsetX, y: document.registration.gridOffsetY,
                } : undefined}
                originPoint={grid.originPoint} hoverOrigin={tool === "origin" ? hoverOrigin : undefined}
                zoom={zoom}/>}
            {selecting && <GeometryHitOverlays geometry={document} selection={state.selection} zoom={zoom}
                onSelect={feature => {annotations.close(); lights.select(undefined); commands.select(feature);}}/>}
            {lights.selected && <Circle
                x={gridToImage(lights.selected.position, document.registration).x}
                y={gridToImage(lights.selected.position, document.registration).y}
                radius={lights.selected.rangeMeters / document.grid.metersPerCell * document.registration.pixelsPerCell}
                fill={!lightingPreview && lights.selected.enabled ? `${lights.selected.color}22` : undefined}
                stroke={lights.selected.enabled ? lights.selected.color : "#9ca3af"}
                strokeWidth={2 / zoom} dash={[6 / zoom, 4 / zoom]} listening={false}/>}
            {lights.displayLights.map(light => {
                const point = gridToImage(light.position, document.registration);
                return <Circle key={light.id} x={point.x} y={point.y} radius={8 / zoom}
                    fill={light.enabled ? light.color : "#6b7280"}
                    stroke={lights.selectedId === light.id ? "white" : "#111827"}
                    strokeWidth={2 / zoom} draggable={selecting} listening={selecting || tool === "light"}
                    onClick={event => {
                        event.cancelBubble = true;
                        commands.select();
                        annotations.close();
                        lights.select(light.id);
                        if (tool === "light") onSelectTool("select", true);
                    }}
                    onDragMove={event => lights.previewChange(light.id, {position: imageToGrid({
                        x: Math.max(0, Math.min(image.width, event.target.x())),
                        y: Math.max(0, Math.min(image.height, event.target.y())),
                    }, document.registration)})}
                    onDragEnd={event => {
                        event.cancelBubble = true;
                        lights.move(light.id, {
                            x: Math.max(0, Math.min(image.width, event.target.x())),
                            y: Math.max(0, Math.min(image.height, event.target.y())),
                        });
                    }}/>;
            })}
            {annotationInteraction && annotations.annotations.map(annotation => {
                const position = gridToImage(annotation.position, document.registration);
                return <AnnotationMarker key={annotation.id} annotation={annotation} x={position.x} y={position.y}
                    zoom={zoom} selected={annotations.selectedId === annotation.id}
                    draggable={selecting && annotation.id !== annotations.draft?.id
                        && !(annotations.selectedId === annotation.id && annotations.editing)}
                    onClick={event => {
                        event.cancelBubble = true;
                        commands.select();
                        lights.select(undefined);
                        if (annotations.selectedId !== annotation.id) annotations.select(annotation.id);
                    }}
                    onTap={event => {
                        event.cancelBubble = true;
                        commands.select();
                        lights.select(undefined);
                        if (annotations.selectedId !== annotation.id) annotations.select(annotation.id);
                    }}
                    onDragStart={() => {annotations.select(annotation.id); annotations.setDragging(true);}}
                    onDragEnd={event => {
                        event.cancelBubble = true;
                        const x = Math.max(0, Math.min(image.width, event.target.x()));
                        const y = Math.max(0, Math.min(image.height, event.target.y()));
                        annotations.move(annotation.id, {x, y});
                    }}/>;
            })}
            {selecting && <SelectedGeometryOverlay controller={geometry} targetGeometry={transitionTargetGeometry}
                onPreviewGeometry={previewGeometry}
                targetLevelName={transitionTargetName}
                snappingEnabled={snappingEnabled} zoom={zoom}/>}
            {drawing && <GeometryDraftOverlay geometry={document} tool={state.tool} wallDraft={state.wallDraft}
                transitionDraft={state.transitionDraft}
                doorDraft={state.doorDraft} cursorSnap={cursorSnap?.tool === tool ? cursorSnap.snap : undefined}
                zoom={zoom}/>}
        </>}
        renderOverlay={(transform, size) => annotations.selected && annotationInteraction && !annotations.dragging &&
            <AnnotationPopover key={`${annotations.selected.id}:${revision}:${annotations.editing}`}
                annotation={annotations.selected} editable editing={annotations.editing}
                isNew={annotations.draft?.id === annotations.selected.id}
                anchor={annotationScreenPosition(annotations.selected.position, document.registration, transform)}
                viewport={size} onEdit={() => annotations.setEditing(true)} onSave={annotations.save}
                onDelete={() => annotations.remove(annotations.selected!.id)} onClose={annotations.close}/>}/>
}
